import React, { useState } from 'react';
import { BarChart3, Plus, Trash2, DollarSign, Users, Image } from 'lucide-react';
import API from '../api/axios';
import toast from 'react-hot-toast';

const emptyOption = () => ({ name: '', price: '', image: null, preview: null });

const CreateSurveyModal = ({ groupId, onClose, onSuccess }) => {
  const [form, setForm] = useState({ title: '', details: '', type: 'count', deadline: '' });
  const [options, setOptions] = useState([emptyOption(), emptyOption()]);
  const [loading, setLoading] = useState(false);

  const addOption = () => setOptions(prev => [...prev, emptyOption()]);
  const removeOption = (i) => setOptions(prev => prev.filter((_, idx) => idx !== i));
  const updateOption = (i, key, val) => setOptions(prev => prev.map((o, idx) => idx === i ? { ...o, [key]: val } : o));

  const handleOptionImage = (i, e) => {
    const file = e.target.files[0];
    if (file) {
      updateOption(i, 'image', file);
      updateOption(i, 'preview', URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.deadline) { toast.error('العنوان والموعد مطلوبان'); return; }
    if (options.some(o => !o.name)) { toast.error('يرجى ملء جميع أسماء الخيارات'); return; }
    if (form.type === 'payment' && options.some(o => !o.price)) { toast.error('يرجى إدخال سعر كل خيار'); return; }

    const fd = new FormData();
    fd.append('groupId', groupId);
    fd.append('title', form.title);
    fd.append('details', form.details);
    fd.append('type', form.type);
    fd.append('deadline', form.deadline);

    const optionsData = options.map(({ name, price }) => ({ name, price: price ? Number(price) : null }));
    fd.append('options', JSON.stringify(optionsData));

    options.forEach((opt, i) => {
      if (opt.image) fd.append(`option_image_${i}`, opt.image);
    });

    setLoading(true);
    try {
      await API.post('/surveys', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('تم إنشاء الاستطلاع 📊');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الإنشاء');
    } finally { setLoading(false); }
  };

  const minDate = new Date().toISOString().slice(0, 16);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title"><BarChart3 size={18} style={{ display: 'inline', marginLeft: '8px' }} />إضافة استطلاع</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Type Selector */}
          <div className="form-group">
            <label className="form-label">نوع الاستطلاع *</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                { id: 'count', label: 'تقليدي', desc: 'لمعرفة عدد الأفراد', icon: <Users size={20} /> },
                { id: 'payment', label: 'مالي', desc: 'يشمل الأسعار والدفع', icon: <DollarSign size={20} /> }
              ].map(t => (
                <button key={t.id} type="button"
                  onClick={() => setForm({ ...form, type: t.id })}
                  style={{
                    padding: '14px', borderRadius: 'var(--radius-md)',
                    border: `2px solid ${form.type === t.id ? 'var(--primary)' : 'var(--border)'}`,
                    background: form.type === t.id ? 'rgba(99,102,241,0.1)' : 'var(--bg-elevated)',
                    color: 'var(--text-primary)', textAlign: 'center', cursor: 'pointer', transition: 'var(--transition)'
                  }}
                >
                  <div style={{ color: form.type === t.id ? 'var(--primary-light)' : 'var(--text-muted)', marginBottom: '6px' }}>{t.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.label}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px' }}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">عنوان الاستطلاع *</label>
            <input type="text" className="form-input" placeholder="مثال: فين نروح؟"
              value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>

          <div className="form-group">
            <label className="form-label">تفاصيل (اختياري)</label>
            <textarea className="form-textarea" placeholder="تفاصيل إضافية..." rows={2}
              value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} />
          </div>

          <div className="form-group">
            <label className="form-label">موعد الانتهاء *</label>
            <input type="datetime-local" className="form-input" min={minDate}
              value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} dir="ltr" />
          </div>

          {/* Options */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label className="form-label" style={{ margin: 0 }}>الخيارات ({options.length})</label>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addOption}>
                <Plus size={14} /> إضافة خيار
              </button>
            </div>

            {options.map((opt, i) => (
              <div key={i} style={{
                padding: '12px', background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-md)', marginBottom: '10px',
                border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  {/* Image upload for payment type */}
                  {form.type === 'payment' && (
                    <label style={{ cursor: 'pointer', flexShrink: 0 }}>
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleOptionImage(i, e)} />
                      <div style={{
                        width: '48px', height: '48px', borderRadius: 'var(--radius-sm)',
                        background: opt.preview ? `url(${opt.preview}) center/cover` : 'var(--bg-card)',
                        border: '1px dashed var(--border-light)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--text-muted)'
                      }}>
                        {!opt.preview && <Image size={16} />}
                      </div>
                    </label>
                  )}

                  <div style={{ flex: 1 }}>
                    <input type="text" className="form-input" placeholder={`اسم الخيار ${i + 1}`}
                      value={opt.name} onChange={e => updateOption(i, 'name', e.target.value)}
                      style={{ marginBottom: form.type === 'payment' ? '8px' : 0 }} />
                    {form.type === 'payment' && (
                      <input type="number" className="form-input" placeholder="السعر (ج.م)"
                        value={opt.price} onChange={e => updateOption(i, 'price', e.target.value)}
                        min="0" dir="ltr" />
                    )}
                  </div>

                  {options.length > 2 && (
                    <button type="button" className="btn btn-danger btn-sm" style={{ padding: '6px', flexShrink: 0 }}
                      onClick={() => removeOption(i)}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>إلغاء</button>
            <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={loading}>
              {loading ? <div className="spinner spinner-sm" /> : <BarChart3 size={16} />}
              {loading ? 'جاري الإنشاء...' : 'إنشاء الاستطلاع'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSurveyModal;
